/**
 * `GET /api/content/topics` — the foundation route that four places need: the
 * practice setup form, the flashcards deck picker, the progress chapter/topic
 * tables and the home page's counts.
 *
 * It lives here rather than in a feature because the payload is identical for
 * all of them. One hook means one query key (`["content", "topics"]`) and one
 * fetch per session; each feature used to cache the same bytes under its own
 * key (`["practice","topics"]`, `["flashcards","topics"]`,
 * `["progress","topics"]`) and fetch them again.
 */
import { useQuery } from "@tanstack/react-query";
import type { BookRef, Localized, Topic } from "@shared/types";
import { api } from "./client";

export interface ChapterDto {
  number: number;
  title: Localized;
  book: BookRef;
}

export interface ContentCounts {
  questions: number;
  selectableQuestions: number;
  flashcards: number;
  formulas: number;
  topics: number;
}

/** When a topic of a not yet opened lecture opens; `null` = closed by the teacher (P1-12). */
export interface TopicLock {
  opensAt: number | null;
}

export interface TopicsResponse {
  chapters: ChapterDto[];
  topics: Topic[];
  counts: ContentCounts;
  /** Topics this student cannot practise yet, by id (P1-12). Empty for a teacher. */
  locked?: Record<string, TopicLock>;
}

/**
 * The taxonomy changes only when the content bank is redeployed, but `locked` changes when a
 * lecture opens (P1-12), so the cache is kept short.
 */
export const TOPICS_STALE_TIME = 60_000;
export const topicsQueryKey = ["content", "topics"] as const;

export function useTopicsAndChapters() {
  return useQuery({
    queryKey: topicsQueryKey,
    queryFn: () => api.get<TopicsResponse>("/api/content/topics"),
    staleTime: TOPICS_STALE_TIME,
  });
}
